<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import AppIcon from './AppIcon.vue'

const { t } = useI18n()
const toasts = useToastStore()

const ICONS: Record<string, string> = {
  ok: 'check',
  danger: 'alert',
  warn: 'alert',
  info: 'info',
}
</script>

<template>
  <div class="toasts" role="status" aria-live="polite">
    <TransitionGroup name="toast">
      <div v-for="toast in toasts.toasts" :key="toast.id" class="toast" :class="`toast--${toast.tone}`">
        <AppIcon :name="ICONS[toast.tone]" :size="16" class="toast__icon" />
        <div class="toast__content">
          <p class="toast__title">{{ toast.title }}</p>
          <p v-if="toast.detail" class="toast__detail">{{ toast.detail }}</p>
        </div>
        <button class="toast__close" type="button" :aria-label="t('header.dismiss')" @click="toasts.dismiss(toast.id)">
          <AppIcon name="close" :size="13" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  inset-inline-end: 1rem;
  bottom: 1rem;
  z-index: 80;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: min(380px, calc(100vw - 2rem));
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.7rem 0.8rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.toast--ok { border-inline-start-color: var(--ok-fg); }
.toast--danger { border-inline-start-color: var(--danger-fg); }
.toast--warn { border-inline-start-color: var(--warn-fg); }
.toast--info { border-inline-start-color: var(--info-fg); }

.toast--ok .toast__icon { color: var(--ok-fg); }
.toast--danger .toast__icon { color: var(--danger-fg); }
.toast--warn .toast__icon { color: var(--warn-fg); }
.toast--info .toast__icon { color: var(--info-fg); }

.toast__icon { flex: none; margin-top: 1px; }
.toast__content { flex: 1; min-width: 0; }
.toast__title { margin: 0; font-size: 0.8125rem; font-weight: 600; }
.toast__detail { margin: 0.1rem 0 0; font-size: 0.75rem; color: var(--text-muted); word-break: break-word; }

.toast__close {
  flex: none;
  padding: 0.15rem;
  color: var(--text-subtle);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.toast__close:hover { color: var(--text); }

.toast-enter-active, .toast-leave-active { transition: all 180ms ease; }
.toast-enter-from { opacity: 0; transform: translateX(16px); }
.toast-leave-to { opacity: 0; transform: translateX(16px); }
</style>
